import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { VersionActionButtonsProps } from "./types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  RotateCcw,
  GitCompare,
  Trash2,
  Send,
  Eye,
  MoreHorizontal,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";

export const VersionActionButtons: React.FC<VersionActionButtonsProps> = ({
  version,
  isCurrent,
  onRestore,
  onCompare,
  onDelete,
  onPublish,
  onViewDetails,
  size = "sm",
  variant = "inline",
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  const handleRestore = () => {
    setShowRestoreDialog(true);
  };

  const handleConfirmRestore = () => {
    onRestore?.();
    setShowRestoreDialog(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.();
    setShowDeleteDialog(false);
  };

  const handlePublish = () => {
    if (version.isPublished) {
      // Already published, no confirmation needed
      onPublish?.();
    } else {
      // Show confirmation for publishing
      setShowPublishDialog(true);
    }
  };

  const handleConfirmPublish = () => {
    onPublish?.();
    setShowPublishDialog(false);
  };

  const handleCompare = () => {
    onCompare?.();
  };

  const handleViewDetails = () => {
    onViewDetails?.();
  };

  // Define button configurations
  const buttons = [
    {
      key: "view",
      label: "View Details",
      icon: <Eye className="w-4 h-4" />,
      onClick: handleViewDetails,
      show: true,
      variant: "outline" as const,
      disabled: false,
    },
    {
      key: "compare",
      label: "Compare",
      icon: <GitCompare className="w-4 h-4" />,
      onClick: handleCompare,
      show: !!onCompare,
      variant: "outline" as const,
      disabled: false,
    },
    {
      key: "restore",
      label: isCurrent ? "Current Version" : "Restore",
      icon: <RotateCcw className="w-4 h-4" />,
      onClick: handleRestore,
      show: !!onRestore,
      variant: "outline" as const,
      disabled: isCurrent,
    },
    {
      key: "publish",
      label: version.isPublished ? "Published" : "Publish",
      icon: version.isPublished ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <Send className="w-4 h-4" />
      ),
      onClick: handlePublish,
      show: !!onPublish,
      variant: version.isPublished
        ? ("outline" as const)
        : ("default" as const),
      disabled: version.isPublished,
    },
    {
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDelete,
      show: !!onDelete && !isCurrent,
      variant: "destructive" as const,
      disabled: isCurrent,
    },
  ];

  const visibleButtons = buttons.filter((button) => button.show);

  if (visibleButtons.length === 0) {
    return null;
  }

  const renderInlineButtons = () => (
    <div className="flex items-center space-x-1">
      {visibleButtons.slice(0, 3).map((button) => (
        <Button
          key={button.key}
          size={size}
          variant={button.variant}
          onClick={button.onClick}
          disabled={button.disabled}
          className={cn(
            "flex-shrink-0",
            size === "sm" && "h-7 px-2",
            button.key === "delete" &&
              "text-destructive hover:text-destructive-foreground",
          )}
          aria-label={button.label}
          title={button.label}
        >
          {button.icon}
          <span className="sr-only">{button.label}</span>
        </Button>
      ))}

      {visibleButtons.length > 3 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size={size}
              variant="outline"
              className={cn("flex-shrink-0", size === "sm" && "h-7 px-2")}
              aria-label="More actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {visibleButtons.slice(3).map((button) => (
              <DropdownMenuItem
                key={button.key}
                onClick={button.onClick}
                disabled={button.disabled}
                className={cn(
                  button.key === "delete" &&
                    "text-destructive focus:text-destructive",
                )}
              >
                {button.icon}
                <span className="ml-2">{button.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  const renderDropdownMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant="outline"
          className={cn("flex-shrink-0", size === "sm" && "h-7 px-2")}
          aria-label="Version actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {visibleButtons.map((button, index) => (
          <React.Fragment key={button.key}>
            <DropdownMenuItem
              onClick={button.onClick}
              disabled={button.disabled}
              className={cn(
                button.key === "delete" &&
                  "text-destructive focus:text-destructive",
              )}
            >
              {button.icon}
              <span className="ml-2">{button.label}</span>
            </DropdownMenuItem>
            {index === visibleButtons.length - 2 && button.key !== "delete" && (
              <DropdownMenuSeparator />
            )}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {/* Action Buttons */}
      {variant === "inline" ? renderInlineButtons() : renderDropdownMenu()}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span>Delete Version</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete version {version.version}?
              {version.name && (
                <span className="block mt-1 font-medium">"{version.name}"</span>
              )}
              <span className="block mt-2 text-sm">
                This action cannot be undone. All data associated with this
                version will be permanently removed.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <RotateCcw className="w-5 h-5 text-primary" />
              <span>Restore Version</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to restore to version {version.version}?
              {version.name && (
                <span className="block mt-1 font-medium">"{version.name}"</span>
              )}
              <span className="block mt-2 text-sm">
                This will replace your current project state with the selected
                version. Any unsaved changes will be lost.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRestore}>
              Restore Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <Send className="w-5 h-5 text-primary" />
              <span>Publish Version</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish version {version.version}?
              {version.name && (
                <span className="block mt-1 font-medium">"{version.name}"</span>
              )}
              <span className="block mt-2 text-sm">
                Publishing will make this version publicly available and prevent
                further modifications. This action can be useful for creating
                stable releases.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPublish}>
              Publish Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VersionActionButtons;
